import React, { useState, useEffect } from 'react';
import { collection, query, getDocs } from 'firebase/firestore';
import { db } from '../firebase';

export default function ContactLogsModal({ serverId, serviceUserId, onClose }) {
    const [logs, setLogs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    useEffect(() => {
        const fetchLogs = async () => {
            setLoading(true);
            try {
                const ref = collection(db, 'servers', serverId, 'serviceUsers', serviceUserId, 'contactLogs');
                const snap = await getDocs(query(ref));
                
                const loaded = snap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
                
                loaded.sort((a, b) => {
                    const getMs = (val) => {
                        if (!val) return 0;
                        if (typeof val.toDate === 'function') return val.toDate().getTime();
                        if (val.seconds) return val.seconds * 1000;
                        return new Date(val).getTime() || 0;
                    };
                    const dateA = getMs(a.date || a.timestamp || a.createdAt);
                    const dateB = getMs(b.date || b.timestamp || b.createdAt);
                    return dateB - dateA; // Newest first
                });
                
                setLogs(loaded);
            } catch (err) {
                console.error("Error fetching contact logs:", err);
                setError("Failed to load contact logs.");
            } finally {
                setLoading(false);
            }
        };

        if (serverId && serviceUserId) {
            fetchLogs();
        }
    }, [serverId, serviceUserId]);

    return (
        <div style={styles.modalOverlay} onClick={onClose}>
            <div style={styles.modalContent} onClick={e => e.stopPropagation()}>
                <div style={styles.modalHeader}>
                    <div>
                        <h2 style={{ margin: 0, fontSize: '20px', color: '#111827' }}>Contact Logs</h2>
                        <p style={{ margin: '4px 0 0', color: '#6b7280', fontSize: '14px' }}>Communications with family and professionals</p>
                    </div>
                    <button onClick={onClose} style={styles.closeBtn}>×</button>
                </div>

                <div style={styles.scrollArea}>
                    {loading ? (
                        <div style={{ padding: '40px', textAlign: 'center', color: '#6b7280' }}>Loading logs...</div>
                    ) : error ? (
                        <div style={{ padding: '40px', textAlign: 'center', color: '#ef4444' }}>{error}</div>
                    ) : logs.length === 0 ? (
                        <div style={styles.emptyState}>
                            <div style={{ fontSize: '48px', marginBottom: '16px' }}>📞</div>
                            <h3 style={{ margin: '0 0 8px 0', color: '#374151' }}>No Contact Logs</h3>
                            <p style={{ margin: 0, color: '#6b7280' }}>There are no communication logs recorded for this service user.</p>
                        </div>
                    ) : (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                            {logs.map((log, index) => {
                                const contactType = log.contactType || log.type || 'General Contact';
                                const method = log.contactMethod || log.method || 'Unknown Method';
                                const person = log.personContacted || log.name || 'Unknown Person';
                                const notes = log.notes || log.details || log.summary || 'No details provided';
                                
                                return (
                                    <div key={log.id || index} style={styles.recordCard}>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '16px' }}>
                                            <div>
                                                <h3 style={{ margin: 0, fontSize: '16px', color: '#111827' }}>{contactType} with {person}</h3>
                                                <div style={{ fontSize: '13px', color: '#6b7280', marginTop: '4px' }}>
                                                    Method: {method}
                                                </div>
                                            </div>
                                            <div style={{ fontSize: '12px', color: '#4b5563', fontWeight: 'bold' }}>
                                                {log.date || log.timestamp || log.createdAt ? new Date((log.date || log.timestamp || log.createdAt)?.seconds ? (log.date || log.timestamp || log.createdAt).seconds * 1000 : (log.date || log.timestamp || log.createdAt)).toLocaleString() : 'N/A'}
                                            </div>
                                        </div>
                                        
                                        <div style={{ backgroundColor: '#f9fafb', padding: '12px', borderRadius: '6px', fontSize: '14px', color: '#374151', whiteSpace: 'pre-wrap' }}>
                                            {notes}
                                        </div>
                                        
                                        <div style={{ marginTop: '12px', fontSize: '12px', color: '#9ca3af' }}>
                                            Logged by: {log.author || log.createdBy || log.loggedBy || 'System'}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

const styles = {
    modalOverlay: { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 1000 },
    modalContent: { backgroundColor: 'white', borderRadius: '12px', width: '90%', maxWidth: '700px', maxHeight: '90vh', display: 'flex', flexDirection: 'column', boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1)' },
    modalHeader: { padding: '24px', borderBottom: '1px solid #e5e7eb', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' },
    closeBtn: { background: 'none', border: 'none', fontSize: '24px', color: '#9ca3af', cursor: 'pointer', lineHeight: 1 },
    scrollArea: { padding: '24px', overflowY: 'auto', flex: 1, backgroundColor: '#f9fafb' },
    emptyState: { display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '40px 0', textAlign: 'center', backgroundColor: 'white', borderRadius: '8px', border: '1px dashed #d1d5db' },
    recordCard: { backgroundColor: 'white', border: '1px solid #e5e7eb', borderRadius: '8px', padding: '20px', boxShadow: '0 1px 2px rgba(0,0,0,0.05)' }
};
